import { APP_INITIALIZER, NgModule, provideZoneChangeDetection } from '@angular/core';
import { BrowserModule } from '@angular/platform-browser';
import { BrowserAnimationsModule } from '@angular/platform-browser/animations';
import { FormsModule } from '@angular/forms';
import { provideHttpClient } from '@angular/common/http';
import { AutoCompleteModule } from 'primeng/autocomplete';
import { ButtonModule } from 'primeng/button';
import { DialogModule } from 'primeng/dialog';
import { InputNumberModule } from 'primeng/inputnumber';
import { MenuModule } from 'primeng/menu';
import { PopoverModule } from 'primeng/popover';
import { TableModule } from 'primeng/table';
import { SelectModule } from 'primeng/select';
import { TextareaModule } from 'primeng/textarea';
import { ToastModule } from 'primeng/toast';
import { MessageService } from 'primeng/api';
import { providePrimeNG } from 'primeng/config';

import { AppRoutingModule } from './app-routing-module';
import { AppComponent } from './app.component';
import { HeaderComponent } from './layout/header/header.component';
import { FooterComponent } from './layout/footer/footer.component';
import { BreadcrumbComponent } from './layout/breadcrumb/breadcrumb.component';
import { DivisionLandingComponent } from './features/division-landing/division-landing.component';
import { ReportsHubComponent } from './features/reports-hub/reports-hub.component';
import { ReportViewerComponent } from './features/reports/report-viewer/report-viewer.component';
import { TreasuryComponent } from './features/treasury/treasury.component';
import { TreasuryStatusComponent } from './features/treasury/treasury-status.component';
import { TreasuryRatesComponent } from './features/treasury/treasury-rates.component';
import { StaticValuesComponent } from './features/static-values/static-values.component';
import { ApprovalDashboardComponent } from './features/approvals/approval-dashboard/approval-dashboard.component';
import { AuthService } from './auth/auth.service';
import { AgriFinOpsTheme } from '../theme/agrifinops-theme';

/**
 * Runs before the app renders. Blocks bootstrap until MSAL has established an active account.
 */
export function initializeAuth(auth: AuthService): () => Promise<void> {
  return () => auth.initialize();
}

@NgModule({
  declarations: [
    AppComponent,
    HeaderComponent,
    FooterComponent,
    BreadcrumbComponent,
    DivisionLandingComponent,
    ReportsHubComponent,
    ReportViewerComponent,
    TreasuryComponent,
    TreasuryStatusComponent,
    TreasuryRatesComponent,
    StaticValuesComponent,
    ApprovalDashboardComponent
  ],
  imports: [
    BrowserModule,
    BrowserAnimationsModule,
    FormsModule,
    AutoCompleteModule,
    ButtonModule,
    DialogModule,
    InputNumberModule,
    MenuModule,
    PopoverModule,
    TableModule,
    SelectModule,
    TextareaModule,
    ToastModule,
    AppRoutingModule
  ],
  providers: [
    MessageService,
    // Angular 21 bootstraps *zoneless* by default, so without this, the views
    // never refresh after an async callback (HTTP responses, promises) mutates
    // component state — e.g. the report table would spin forever. These
    // components hold plain mutable fields rather than signals, so they need
    // Zone.js-driven change detection.
    provideZoneChangeDetection({ eventCoalescing: true }),
    provideHttpClient(),
    AuthService,
    {
      provide: APP_INITIALIZER,
      useFactory: initializeAuth,
      deps: [AuthService],
      multi: true
    },
    // Applies the Figma-derived theme plus the app-level overrides in
    // src/theme/agrifinops-theme.ts. `.dark-mode` on a host element
    // (e.g. <html>) switches to the dark colour scheme.
    providePrimeNG({
      theme: {
        preset: AgriFinOpsTheme,
        options: {
          darkModeSelector: '.dark-mode'
        }
      },
      ripple: true
    })
  ],
  bootstrap: [AppComponent]
})
export class AppModule { }
